import type { ApiBooking, ApiBookingUpdate, BookingPricingLine } from "@/types/api";
import { matchedBookingPrice } from "@/types/booking";

export interface BookingCommercialPair {
  carrier: string;
  contract_ref: string;
  named_account?: string;
}

export interface BookingCommercial {
  carrier?: string;
  contract_ref?: string;
  named_account?: string;
  booking_ref?: string;
  pairs: BookingCommercialPair[];
}

function splitCsv(value?: string | null): string[] {
  return (value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

function currentUpdates(updates?: ApiBookingUpdate[]): ApiBookingUpdate[] {
  const rows = updates ?? [];
  const current = rows.filter(
    (update) =>
      update.is_current && update.source?.trim().toLowerCase() !== "graph_mail",
  );
  return current.length > 0
    ? current
    : rows.filter((u) => u.source?.trim().toLowerCase() !== "graph_mail");
}

/**
 * Build carrier/contract pairs the way frontend v1 does: prefer
 * `all_contract_refs` / `all_carriers` (every current update split), then the
 * primary fields, then current updates themselves.
 */
export function resolveBookingCommercial(
  booking: Pick<
    ApiBooking,
    | "carrier"
    | "contract_ref"
    | "all_carriers"
    | "all_contract_refs"
    | "named_accounts"
    | "updates"
    | "booking_ref"
  >,
): BookingCommercial {
  const namedAccounts = booking.named_accounts ?? {};
  const contracts = splitCsv(booking.all_contract_refs ?? booking.contract_ref);
  const alignedCarriers = splitCsv(booking.all_carriers);
  const fallbackCarrier = booking.carrier?.trim() ?? "";

  const pairs: BookingCommercialPair[] = [];
  if (contracts.length > 0) {
    for (let i = 0; i < contracts.length; i += 1) {
      const contract_ref = contracts[i];
      const carrier =
        (booking.all_carriers
          ? alignedCarriers[i]
          : fallbackCarrier) || fallbackCarrier;
      pairs.push({
        carrier,
        contract_ref,
        named_account: namedAccounts[contract_ref],
      });
    }
  }

  if (pairs.length === 0) {
    for (const update of currentUpdates(booking.updates)) {
      const contract_ref = update.contract_ref?.trim();
      if (!contract_ref) continue;
      if (pairs.some((pair) => pair.contract_ref === contract_ref)) continue;
      pairs.push({
        carrier: update.carrier?.trim() || fallbackCarrier,
        contract_ref,
        named_account: namedAccounts[contract_ref],
      });
    }
  }

  const first = pairs[0];
  let booking_ref = booking.booking_ref?.trim() || undefined;
  if (first) {
    const match = currentUpdates(booking.updates).find(
      (update) =>
        update.contract_ref?.trim() === first.contract_ref &&
        update.booking_ref?.trim(),
    );
    if (match?.booking_ref?.trim()) booking_ref = match.booking_ref.trim();
  }

  return {
    carrier: first?.carrier || fallbackCarrier || undefined,
    contract_ref: first?.contract_ref,
    named_account: first?.named_account,
    booking_ref,
    pairs,
  };
}

export interface BookingPricingGroup {
  contractRef: string;
  carrier: string | null;
  subtotal: number;
  currency: string;
  rateCardId?: string;
}

export function groupPricingLines(
  lines: BookingPricingLine[],
): BookingPricingGroup[] {
  const groups = new Map<string, BookingPricingLine[]>();
  for (const line of lines) {
    const key = line.contract_ref?.trim() || "(no contract)";
    const arr = groups.get(key) ?? [];
    arr.push(line);
    groups.set(key, arr);
  }

  return [...groups.entries()].map(([contractRef, groupLines]) => ({
    contractRef,
    carrier: groupLines.find((line) => line.carrier)?.carrier ?? null,
    subtotal: groupLines.reduce((sum, line) => sum + line.price_total, 0),
    currency:
      groupLines.find((line) => line.rate_line_id)?.currency ??
      groupLines[0]?.currency ??
      "USD",
    rateCardId:
      groupLines.find((line) => line.rate_card_id)?.rate_card_id ?? undefined,
  }));
}

/**
 * Keep pricing lines that belong to the booking's currently assigned
 * contracts/carriers (drops superseded rebook-chain snapshots).
 */
export function filterCurrentPricingLines(
  lines: BookingPricingLine[],
  booking: Pick<
    ApiBooking,
    "all_contract_refs" | "contract_ref" | "all_carriers" | "carrier"
  >,
): BookingPricingLine[] {
  if (lines.length === 0) return lines;

  const allowedContracts = new Set(
    splitCsv(booking.all_contract_refs ?? booking.contract_ref),
  );
  const allowedCarriers = new Set(
    splitCsv(booking.all_carriers ?? booking.carrier).map((c) =>
      c.toUpperCase(),
    ),
  );

  if (allowedContracts.size === 0 && allowedCarriers.size === 0) {
    return lines;
  }

  const filtered = lines.filter((line) => {
    if (line.contract_ref?.trim()) {
      return allowedContracts.has(line.contract_ref.trim());
    }
    if (line.carrier?.trim()) {
      return allowedCarriers.has(line.carrier.trim().toUpperCase());
    }
    return true;
  });

  return filtered.length > 0 ? filtered : lines;
}

export interface ResolvedBookingRate {
  carrier?: string;
  contractRef?: string;
  namedAccount?: string;
  rateCardId?: string;
  pricingTotal?: number;
  bookingRef?: string;
}

/** Merge assigned-update commercial identity with live pricing lines. */
export function resolveAssignedRate(
  booking: Pick<
    ApiBooking,
    | "carrier"
    | "contract_ref"
    | "all_carriers"
    | "all_contract_refs"
    | "named_accounts"
    | "updates"
    | "booking_ref"
    | "pricing_total"
    | "pricing_matched"
  >,
  lines: BookingPricingLine[] = [],
): ResolvedBookingRate {
  const commercial = resolveBookingCommercial(booking);
  // Only matched (or agent-priced) contracts contribute to the shipment total —
  // an unmatched "?" placeholder must not inflate the headline figure.
  const groups = groupPricingLines(
    filterMatchedPricingLines(filterCurrentPricingLines(lines, booking)),
  );
  const match =
    groups.find((group) => group.contractRef === commercial.contract_ref) ??
    groups[0];
  const groupTotal = groups.reduce((sum, group) => sum + group.subtotal, 0);
  const pricingTotal =
    matchedBookingPrice(groupTotal) ??
    matchedBookingPrice(booking.pricing_total);

  const contractRef =
    commercial.contract_ref ??
    (match && match.contractRef !== "(no contract)"
      ? match.contractRef
      : undefined);

  return {
    carrier: commercial.carrier || match?.carrier || undefined,
    contractRef,
    namedAccount:
      commercial.named_account ??
      (contractRef ? booking.named_accounts?.[contractRef] : undefined),
    rateCardId: match?.rateCardId,
    pricingTotal,
    bookingRef: commercial.booking_ref,
  };
}

/** Container line with a contract but no matched rate card row (not agent-priced). */
export function isUnmatchedContractRateLine(line: BookingPricingLine): boolean {
  return (
    !line.rate_line_id?.trim() && line.match_reason?.trim() !== "agent_price"
  );
}

/**
 * Drop unmatched contract placeholder lines (the "?" rows with no matched rate
 * line and no agent price) so they never present as a phantom contract split or
 * inflate the shipment total. Falls back to the original list when *nothing* is
 * matched, so a fully-unmatched booking still renders its placeholder instead
 * of collapsing to an empty hero.
 */
export function filterMatchedPricingLines(
  lines: BookingPricingLine[],
): BookingPricingLine[] {
  const matched = lines.filter((line) => !isUnmatchedContractRateLine(line));
  return matched.length > 0 ? matched : lines;
}

/** Pricing lines for the booking's currently assigned contract. */
export function resolveAssignedPricingLines(
  booking: Pick<
    ApiBooking,
    "all_contract_refs" | "contract_ref" | "all_carriers" | "carrier"
  >,
  lines: BookingPricingLine[],
  contractRef?: string | null,
): BookingPricingLine[] {
  const filtered = filterCurrentPricingLines(lines, booking);
  const ref = contractRef?.trim();
  if (ref) {
    const forContract = filtered.filter((line) => line.contract_ref?.trim() === ref);
    if (forContract.length > 0) return forContract;
    // Unmatched contract placeholders stay on the assigned contract even when
    // filterCurrentPricingLines would drop them (legacy alignment).
    const directForContract = lines.filter(
      (line) => line.contract_ref?.trim() === ref,
    );
    if (directForContract.length > 0) return directForContract;
  }
  return filtered;
}
